import { FC } from 'react';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { DisplayOrder, OrderBaseUrls, OrderLineOpenedFrom, ShipmentWithOrderLines } from '@ravago/shared/page-data/models/elements';
import { DateUtils } from '@ravago/shared/page-data/utils/date';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Chip } from '@ravago/shared/radiance/components/chip/components/Chip/Chip';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { SystemPageLink } from '../../content/system-page-link/system-page-link';
import { OrderLineTableMenu } from '../order-lines-table/order-line-table-menu';
import { OrderLineItem } from '../order/order-line-item';

export const MobileShipmentTable: FC<{
   shipment: ShipmentWithOrderLines;
   locale: string;
   acceptWebp?: boolean;
   openedFrom: OrderLineOpenedFrom;
   orderBaseUrls: OrderBaseUrls;
   onOrderLineButtonClick: (orderLine: DisplayOrder) => void;
}> = ({ shipment, locale, acceptWebp, openedFrom, orderBaseUrls, onOrderLineButtonClick }) => {
   const translate = useTranslations();
   const format = useFormatter();

   return (
      <Table rowDividers={false} rounded border tableLayout="auto">
         <TableHead>
            <TableRow>
               <TableHeader colSpan={2}>
                  <Box direction="column" items="end" wrap="wrap" gap="xs">
                     <Chip
                        type={
                           <Typography variant="small" component="span" color="strong">
                              {translate('shipment.delivery-date')}
                           </Typography>
                        }
                        label={
                           <Typography variant="small-highlight" component="span" align="left" color="strong">
                              {DateUtils.transformOrderDeliveryDate(format, shipment.orderLines[0].deliveryDate)}
                           </Typography>
                        }
                     />
                     <SystemPageLink
                        baseRoute={orderBaseUrls.shipmentBaseUrl}
                        locale={locale}
                        type={SystemPageType.SHIPMENT}
                        slug={shipment.shipmentId.toString()}
                     >
                        <Typography variant="body-highlight" component="span" underline color="primary">
                           {translate('shipment.shipment')} {shipment.shipmentId}
                        </Typography>
                     </SystemPageLink>
                  </Box>
               </TableHeader>
            </TableRow>
         </TableHead>
         {shipment.orderLines.map((orderLine) => (
            <TableBody key={orderLine.orderNumber}>
               <TableRow>
                  <TableCell colSpan={2}>
                     <Box justify="between">
                        <Box onClick={() => onOrderLineButtonClick(orderLine)}>
                           <OrderLineItem order={orderLine} openedFrom={openedFrom} acceptWebp={acceptWebp} />
                        </Box>
                        <Box justify="end">
                           <OrderLineTableMenu
                              orderLine={orderLine}
                              shipmentId={shipment.shipmentId}
                              locale={locale}
                              openedFrom={openedFrom}
                              orderBaseUrls={orderBaseUrls}
                              onOrderLineButtonClick={onOrderLineButtonClick}
                           />
                        </Box>
                     </Box>
                  </TableCell>
               </TableRow>
               <TableRow onClick={() => onOrderLineButtonClick(orderLine)}>
                  <TableCell>
                     <Box direction="column" gap="3xs">
                        <Typography variant="small">
                           {translate('my-orders.table.order-line')} {orderLine.orderNumber}
                        </Typography>
                        <Typography variant="small">
                           {translate('my-orders.table.order-ref')} {orderLine.customerOrderReference || '-'}
                        </Typography>
                     </Box>
                  </TableCell>
               </TableRow>
            </TableBody>
         ))}
      </Table>
   );
};
